// Single decision point for where the auth credential lives. The login "Remember me"
// choice selects the store. `persist` itself always lives in localStorage, because it
// must be readable before the store is known.

const PERSIST_KEY = "persist";
const TOKEN_KEY = "token";
const USER_KEY = "user";
const COMPANY_KEY = "companyData";

export interface Session {
  token: string;
  user: any;
}

const safeParse = (value: string | null) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

// Storage access itself can throw, not just the parse — a browser with storage
// disabled or in a locked-down privacy mode. getSession runs inside AuthProvider's
// useState initialiser, i.e. during render, where an uncaught throw is a blank app
// rather than a logged error. Every access goes through these two guards so the worst
// case degrades to "not logged in".
//
// The store is always passed explicitly rather than defaulted. A default of `store()`
// would make isPersistent -> readFrom -> store -> isPersistent mutually recursive.
const readFrom = (from: Storage, key: string): string | null => {
  try {
    return from.getItem(key);
  } catch {
    return null;
  }
};

const writeTo = (to: Storage, key: string, value: string): void => {
  try {
    to.setItem(key, value);
  } catch {
    // Nothing useful to do: the session simply will not survive a reload.
  }
};

export const isPersistent = (): boolean =>
  safeParse(readFrom(localStorage, PERSIST_KEY)) === true;

export const setPersistent = (value: boolean): void => {
  writeTo(localStorage, PERSIST_KEY, JSON.stringify(value));
};

const store = (): Storage => (isPersistent() ? localStorage : sessionStorage);

// Call setPersistent first: this writes to whichever store that choice selects.
export const setSession = ({ token, user }: Session): void => {
  const target = store();
  writeTo(target, TOKEN_KEY, JSON.stringify(token));
  writeTo(target, USER_KEY, JSON.stringify(user));
  writeTo(target, COMPANY_KEY, JSON.stringify(user));
};

export const setToken = (token: string): void => {
  writeTo(store(), TOKEN_KEY, JSON.stringify(token));
};

export const getSession = (): Session | null => {
  const active = store();
  const token = safeParse(readFrom(active, TOKEN_KEY));
  if (!token) return null;

  return { token, user: safeParse(readFrom(active, USER_KEY)) || {} };
};

// A 401 for a token we are no longer holding tells us nothing about the current session —
// it belongs to a request that outlived the token it was signed with. Acting on it would
// tear down whatever session has since been established. Returns false when no token was
// sent and after clearSession(), so a burst of 401s cannot log out repeatedly.
export const isCurrentToken = (authorizationHeader?: string): boolean => {
  const sent = String(authorizationHeader ?? "")
    .replace(/^Bearer\s+/i, "")
    .trim();
  if (!sent) return false;

  return sent === (getSession()?.token ?? "");
};

// The cached user and companyData objects live alongside the token, so they must follow
// the same store. Named "stored" to avoid colliding with the many local setUser /
// setCompanyData React state setters.
export const getStoredUser = (): any => safeParse(readFrom(store(), USER_KEY));

export const setStoredUser = (user: any): void => {
  writeTo(store(), USER_KEY, JSON.stringify(user));
};

export const getStoredCompanyData = (): any =>
  safeParse(readFrom(store(), COMPANY_KEY));

export const setStoredCompanyData = (data: any): void => {
  writeTo(store(), COMPANY_KEY, JSON.stringify(data));
};

// Clears both stores so logout cannot strand a credential in the one it was not using.
export const clearSession = (): void => {
  [localStorage, sessionStorage].forEach((target) => {
    [TOKEN_KEY, USER_KEY, COMPANY_KEY].forEach((key) => {
      try {
        target.removeItem(key);
      } catch {
        // Storage unavailable: there is nothing stored to clear either.
      }
    });
  });
};
